import { get } from 'lodash';

const LocalStorageServices = (() => {
  const JWT_TOKEN = 'jwtToken';
  const ACCESS_TOKEN = 'accessToken';
  const EXPIRES_AT = 'expiresAt';
  const SSO_CONFIG = 'ssoConfig';
  const USER_TYPE = 'userType';

  /* COMMON UTILS */
  const _getJWTToken = () => {
    const token = localStorage.getItem(JWT_TOKEN);
    try {
      return JSON.parse(token);
    } catch (err) {
      return token;
    }
  };

  /* SET TOKENS */

  const _setAccessToken = (tokenObj) => {
    localStorage.setItem(JWT_TOKEN, tokenObj);
  };

  const _setSsoAccessTokenAndExpiry = (jwtObj) => {
    if (process.env.REACT_APP_USE_EXT_AUTH !== 'true') return;

    const _accessToken = get(jwtObj, 'data.access_token', null);
    const _expiresAt = get(jwtObj, 'data.expires_on', null);

    localStorage.setItem(ACCESS_TOKEN, _accessToken);
    localStorage.setItem(EXPIRES_AT, _expiresAt);
  };

  /* GET TOKENS */

  const _getAccessToken = () => {
    const tokenObj = _getJWTToken();
    if (tokenObj) {
      return typeof tokenObj === 'object' ? tokenObj?.value : tokenObj;
    }
    return null;
  };

  const _getRefreshToken = () => {
    const tokenObj = _getJWTToken();
    return typeof tokenObj === 'object' ? tokenObj?.refresh : null;
  };

  const _getSsoAccessToken = () => {
    return localStorage.getItem(ACCESS_TOKEN) || null;
  };

  const _getTokenExpirationTime = () => {
    const tokenItems = _getJWTToken();
    return tokenItems ? tokenItems.expiry : null;
  };

  /* CLEAR TOKENS */

  const _clearToken = () => {
    localStorage.removeItem(JWT_TOKEN);
  };

  const _clearSSOToken = () => {
    localStorage.removeItem(ACCESS_TOKEN);
    localStorage.removeItem(EXPIRES_AT);
  };

  /* GET/SET CONFIG */

  const _setSSOConfig = (ssoAuthConfig) => {
    const { ENABLE: isSSOEnabled } = ssoAuthConfig || {};
    if (isSSOEnabled) {
      localStorage.setItem(SSO_CONFIG, JSON.stringify(ssoAuthConfig));
    } else {
      localStorage.removeItem(SSO_CONFIG);
    }
  };

  const _getSSOConfig = () => {
    try {
      return JSON.parse(localStorage.getItem(SSO_CONFIG)) || {};
    } catch (e) {
      console.error('failed to parse SSO Config');
      return {};
    }
  };

  const _getUserType = () => {
    return localStorage.getItem(USER_TYPE);
  };

  const _clearSSOKeys = () => {
    localStorage.removeItem(EXPIRES_AT);
    localStorage.removeItem(ACCESS_TOKEN);
    localStorage.removeItem('expiresIn');
    localStorage.removeItem(JWT_TOKEN);
    localStorage.removeItem('isLoggedInWithSSO');
    localStorage.removeItem('isIdpInitiatedFlow');
    localStorage.removeItem(USER_TYPE);
  };

  /* SET/GET/REMOVE LOCAL VALUES */

  const _setLocalStorageValue = (key, value) => {
    localStorage.setItem(`${key}`, value);
  };

  const _getLocalStorageValue = (key) => {
    return localStorage.getItem(key);
  };

  const _removeLocalStorageValue = (key) => {
    localStorage.removeItem(key);
  };

  return {
    setAccessToken: _setAccessToken,
    getAccessToken: _getAccessToken,
    getSSOAccessToken: _getSsoAccessToken,
    getRefreshToken: _getRefreshToken,
    setSsoAccessTokenAndExpiry: _setSsoAccessTokenAndExpiry,
    getTokenExpirationTime: _getTokenExpirationTime,
    clearToken: _clearToken,
    clearSSOToken: _clearSSOToken,
    setSSOConfig: _setSSOConfig,
    getSSOConfig: _getSSOConfig,
    getUserType: _getUserType,
    clearSSOKeys: _clearSSOKeys,
    setLocalValue: _setLocalStorageValue,
    getLocalValue: _getLocalStorageValue,
    removeLocalValue: _removeLocalStorageValue,
  };
})();

export default LocalStorageServices;
